import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatCep, formatCpfCnpj } from '../utils/formatCpfCnpj';
import { buscarPorCep } from '../services/viaCep';
import './Login.css';
import './CriarConta.css';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (protocol === 'https:' || port === '80' || port === '443') return '';
    return `${protocol}//${hostname}:5000`;
  }
  return 'http://localhost:5000';
};

const CriarConta = () => {
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const [empreendimento, setEmpreendimento] = useState({
    nome: '',
    cnpj_cpf: '',
    municipio: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    uf: ''
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [usuario, setUsuario] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (usuario.senha !== usuario.confirmarSenha) {
      setError('As senhas não coincidem.');
      return;
    }
    if (usuario.senha.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${getApiBaseUrl()}/api/auth/criar-conta`, {
        empreendimento: {
          nome: empreendimento.nome.trim(),
          cnpj_cpf: empreendimento.cnpj_cpf.trim() || undefined,
          municipio: empreendimento.municipio.trim() || undefined,
          cep: empreendimento.cep.trim() || undefined,
          logradouro: empreendimento.logradouro.trim() || undefined,
          numero: empreendimento.numero.trim() || undefined,
          complemento: empreendimento.complemento.trim() || undefined,
          bairro: empreendimento.bairro.trim() || undefined,
          uf: empreendimento.uf.trim() || undefined
        },
        usuario: {
          nome: usuario.nome.trim(),
          email: usuario.email.trim(),
          senha: usuario.senha
        }
      });
      if (res.data?.token && res.data?.user) {
        await loginWithSession(res.data.token, res.data.user);
        navigate('/home', { replace: true });
        return;
      }
      setError('Resposta inválida do servidor.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCepBlur = async () => {
    const digits = String(empreendimento.cep || '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const endereco = await buscarPorCep(empreendimento.cep);
      if (!endereco.erro) {
        setEmpreendimento(prev => ({
          ...prev,
          logradouro: endereco.logradouro || prev.logradouro,
          bairro: endereco.bairro || prev.bairro,
          municipio: endereco.localidade || prev.municipio,
          uf: endereco.uf || prev.uf
        }));
      }
    } catch (e) {
      console.warn('Busca CEP:', e);
    } finally {
      setCepLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card glass criar-conta-card">
        <div className="login-header">
          <img src="/logo.png" alt="Green Check" className="login-logo" />
          <h1>Criar conta</h1>
          <p className="text-muted">Dados do empreendimento e do administrador</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form criar-conta-form">
          {error && <div className="error-message">{error}</div>}
          <section className="criar-conta-section">
            <h2 className="criar-conta-section-title">Empreendimento</h2>
            <div className="form-group">
              <label>Nome do empreendimento *</label>
              <input
                type="text"
                className="form-control"
                value={empreendimento.nome}
                onChange={(e) => setEmpreendimento(prev => ({ ...prev, nome: e.target.value }))}
                required
                placeholder="Ex: Fazenda Solar XYZ"
              />
            </div>
            <div className="form-group">
              <label>CNPJ/CPF</label>
              <input
                type="text"
                className="form-control"
                value={empreendimento.cnpj_cpf}
                onChange={(e) => setEmpreendimento(prev => ({ ...prev, cnpj_cpf: formatCpfCnpj(e.target.value) }))}
                placeholder="00.000.000/0000-00 ou 000.000.000-00"
              />
            </div>
            <div className="form-group">
              <label>CEP</label>
              <input
                type="text"
                className="form-control"
                value={empreendimento.cep}
                onChange={(e) => setEmpreendimento(prev => ({ ...prev, cep: formatCep(e.target.value) }))}
                onBlur={handleCepBlur}
                placeholder="00000-000"
                maxLength={9}
              />
              {cepLoading && <span className="form-help">Buscando endereço...</span>}
              <span className="form-help">Preencha o CEP para buscar Estado, Cidade e Endereço (ViaCEP).</span>
            </div>
            {(String(empreendimento.cep || '').replace(/\D/g, '').length === 8 || empreendimento.logradouro || empreendimento.uf) && (
              <div className="criar-conta-address-block">
                <div className="criar-conta-address-row">
                  <div className="form-group">
                    <label>Estado (UF)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={empreendimento.uf}
                      onChange={(e) => setEmpreendimento(prev => ({ ...prev, uf: e.target.value.toUpperCase().slice(0, 2) }))}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group criar-conta-field-flex">
                    <label>Cidade</label>
                    <input
                      type="text"
                      className="form-control"
                      value={empreendimento.municipio}
                      onChange={(e) => setEmpreendimento(prev => ({ ...prev, municipio: e.target.value }))}
                      placeholder="Município"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Endereço</label>
                  <input
                    type="text"
                    className="form-control"
                    value={empreendimento.logradouro}
                    onChange={(e) => setEmpreendimento(prev => ({ ...prev, logradouro: e.target.value }))}
                    placeholder="Rua, avenida, rodovia..."
                  />
                </div>
                <div className="form-group">
                  <label>Bairro</label>
                  <input
                    type="text"
                    className="form-control"
                    value={empreendimento.bairro}
                    onChange={(e) => setEmpreendimento(prev => ({ ...prev, bairro: e.target.value }))}
                    placeholder="Bairro"
                  />
                </div>
                <div className="criar-conta-address-row">
                  <div className="form-group">
                    <label>Número</label>
                    <input
                      type="text"
                      className="form-control"
                      value={empreendimento.numero}
                      onChange={(e) => setEmpreendimento(prev => ({ ...prev, numero: e.target.value }))}
                      placeholder="Nº ou km"
                    />
                  </div>
                  <div className="form-group criar-conta-field-flex">
                    <label>Complemento</label>
                    <input
                      type="text"
                      className="form-control"
                      value={empreendimento.complemento}
                      onChange={(e) => setEmpreendimento(prev => ({ ...prev, complemento: e.target.value }))}
                      placeholder="Sala, bloco..."
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
          <section className="criar-conta-section">
            <h2 className="criar-conta-section-title">Administrador do empreendimento</h2>
            <div className="form-group">
              <label>Seu nome *</label>
              <input
                type="text"
                className="form-control"
                value={usuario.nome}
                onChange={(e) => setUsuario(prev => ({ ...prev, nome: e.target.value }))}
                required
                placeholder="Nome completo"
              />
            </div>
            <div className="form-group">
              <label>E-mail *</label>
              <input
                type="email"
                className="form-control"
                value={usuario.email}
                onChange={(e) => setUsuario(prev => ({ ...prev, email: e.target.value }))}
                required
                placeholder="seu@email.com"
              />
            </div>
            <div className="form-group">
              <label>Senha *</label>
              <input
                type="password"
                className="form-control"
                value={usuario.senha}
                onChange={(e) => setUsuario(prev => ({ ...prev, senha: e.target.value }))}
                required
                minLength={6}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div className="form-group">
              <label>Confirmar senha *</label>
              <input
                type="password"
                className="form-control"
                value={usuario.confirmarSenha}
                onChange={(e) => setUsuario(prev => ({ ...prev, confirmarSenha: e.target.value }))}
                required
                minLength={6}
                placeholder="Repita a senha"
              />
            </div>
          </section>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Criando conta…' : 'Criar conta'}
          </button>
          <p className="login-footer">
            Já tem conta? <Link to="/login">Entrar</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default CriarConta;
